import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

export default function Confirmation() {
  const { state } = useLocation();
  const app = state?.application;

  return (
    <main
      data-testid="confirmation-page"
      className="min-h-screen bg-[#F6F1E8] flex flex-col"
    >
      <header className="px-6 md:px-12 lg:px-24 py-8 flex items-center justify-between border-b border-border">
        <Link to="/" className="flex items-center">
          <img
            src="/chic-logo.webp"
            alt="Chic Wedding Photography"
            className="h-10 md:h-12 w-auto"
          />
        </Link>
        <Link
          to="/"
          data-testid="confirmation-back"
          className="eyebrow link-underline"
        >
          ← Back to the studio
        </Link>
      </header>

      <section className="flex-1 px-6 md:px-12 lg:px-24 py-20 md:py-28 grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="eyebrow text-muted-foreground mb-6 hidden"
          >
            Application Received
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.1 }}
            data-testid="confirmation-headline"
            className="font-serif-display tracking-tight leading-[0.95] text-5xl md:text-7xl lg:text-8xl"
          >
            Thank you{app?.full_name ? `, ${app.full_name.split(" ")[0]}` : ""}.
            <br />
            <span className="italic">We&apos;ll be in touch.</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease, delay: 0.5 }}
            className="mt-12 max-w-xl space-y-6 text-foreground/80 leading-relaxed"
          >
            <p>
              Your application has been received personally. We read every word
              and reply within 24 hours, usually with a short note from Andy
              himself.
            </p>
            <p>
              If our calendars align, we&apos;ll send you a private link to book
              your complimentary studio consultation, the one normally valued
              at £500.
            </p>
            <p className="eyebrow text-muted-foreground">
              In the meantime, follow our quiet archive on Instagram.
            </p>
          </motion.div>
        </div>

        <motion.aside
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.3 }}
          className="lg:col-span-5 border border-border p-8 md:p-10 bg-secondary"
        >
          <p className="eyebrow text-muted-foreground mb-4">Your Application</p>
          <ul className="space-y-4 text-sm">
            {app ? (
              <>
                <Detail label="Reference" value={app.id?.slice(0, 8).toUpperCase()} />
                <Detail label="Name" value={app.full_name} />
                {app.partner_name && <Detail label="Partner" value={app.partner_name} />}
                <Detail label="Email" value={app.email} />
                <Detail label="Telephone" value={app.phone} />
                {app.wedding_date && <Detail label="Date" value={app.wedding_date} />}
                {app.venue && <Detail label="Venue" value={app.venue} />}
                {app.package_tier && (
                  <Detail label="Collection" value={app.package_tier} />
                )}
              </>
            ) : (
              <li className="text-foreground/60">
                We&apos;ve logged your application. Please check your email shortly.
              </li>
            )}
          </ul>
          <div className="hairline my-8" />
          <p className="text-xs text-foreground/60 leading-relaxed">
            A response will arrive from <em>hello@chicwedding.co</em>. If you
            don&apos;t see it within 24 hours, please check your spam folder.
          </p>
        </motion.aside>
      </section>

      <footer className="px-6 md:px-12 lg:px-24 py-8 border-t border-border text-xs text-foreground/60">
        © {new Date().getFullYear()} Chic Wedding Photography · Est. Andy Thomas
      </footer>
    </main>
  );
}

function Detail({ label, value }) {
  return (
    <li className="flex items-baseline justify-between gap-6 border-b border-border pb-3">
      <span className="eyebrow text-muted-foreground">{label}</span>
      <span className="font-serif-display italic text-right text-base">{value}</span>
    </li>
  );
}
